import "./animations.css";
import { useState, useEffect, useRef } from "react";
import Logo from "./components/common/Logo";
import { useConfirm } from "./ConfirmDialog";
import Pipeline from "./Pipeline";
import AutomationsView from "./AutomationsView";
import HeuresView from "./HeuresView";
import Login from "./Login";
import MobileApp from "./MobileApp";
import AppShell from "./AppShell";
import DashboardNew from "./DashboardNew";
import NotifPanel from "./NotifPanel";
import GlobalSearch from "./GlobalSearch";
import Users from "./Users";
import MapView from "./MapView";
import ModalIntervention from "./ModalIntervention";
import ModalClient from "./ModalClient";
import ModalSAV from "./ModalSAV";
import ModalStock from "./ModalStock";
import IntDetail from "./IntDetail";
import ModalAssigner from "./ModalAssigner";
import ModalPV from "./ModalPV";
import { THEMES, getTheme, dark, light } from "./theme";
import UserProfile from "./UserProfile";
import Planning from "./Planning";
import Stats from "./Stats";
import InterventionTechnicien from "./InterventionTechnicien";
import SuggestionsGeo from "./SuggestionsGeo";
import WarningsPanel from "./WarningsPanel";
import EscaladeNotif from "./EscaladeNotif";
import AiChatPanel from "./AiChatPanel";
import ModelesPage from "./ModelesPage";
import { api } from "./api";
import DashboardTechnicien from "./DashboardTechnicien";
import SearchBar from "./SearchBar";
import SettingsView from "./SettingsView";
import StockView from "./StockView";
import PortailTechnicien from "./PortailTechnicien";
import ParcIT from "./ParcIT";
import ExportPDF from "./ExportPDF";
import SAVListView from "./SAVListView";
import { MODULES_PERMS } from "./SettingsView";
import MFAVerify from "./MFAVerify";
import ModalInvitation from "./ModalInvitation";
import SetPassword from "./SetPassword";
import NonConformites from "./NonConformites";
import ChecklistIntervention from "./ChecklistIntervention";
import ZonesCameras from "./ZonesCameras";
import FicheConsigne from "./FicheConsigne";
import IntListView from "./IntListView";
import ParcsVehicules from "./ParcsVehicules";
import TodoView from "./TodoView";
import SignaturePublique from "./SignaturePublique";

const SC = (T) => ({
  "in-progress":{lbl:"En cours",c:T.accent,bg:T.accentDim},
  "pending":{lbl:"En attente",c:T.warning,bg:T.warningDim},
  "completed":{lbl:"Terminé",c:T.success,bg:T.successDim},
  "open":{lbl:"Ouvert",c:T.danger,bg:T.dangerDim},
  "resolved":{lbl:"Résolu",c:T.success,bg:T.successDim},
  // Statuts réels de la BDD
  "brouillon":{lbl:"Brouillon",c:T.muted,bg:`${T.muted}22`},
  "planifiee":{lbl:"Planifié",c:T.warning,bg:T.warningDim},
  "en_cours":{lbl:"En cours",c:T.accent,bg:T.accentDim},
  "terminee":{lbl:"Terminé",c:T.success,bg:T.successDim},
  "annulee":{lbl:"Annulé",c:T.danger,bg:T.dangerDim},
  "ouvert":{lbl:"Ouvert",c:T.danger,bg:T.dangerDim},
  "resolu":{lbl:"Résolu",c:T.success,bg:T.successDim},
  "en_attente":{lbl:"En attente",c:T.warning,bg:T.warningDim},
});
const PC = (T) => ({
  "urgent":{lbl:"Urgent",c:T.danger},
  "high":{lbl:"Haute",c:T.warning},
  "medium":{lbl:"Moyenne",c:T.accent},
  "low":{lbl:"Basse",c:T.muted},
});
const StC = (T) => ({
  "ok":{lbl:"OK",c:T.success},
  "low":{lbl:"Faible",c:T.warning},
  "critical":{lbl:"Critique",c:T.danger},
  "out":{lbl:"Rupture",c:T.danger},
});

const getStockStatus = (qty, min) => qty===0?"out":qty<min?(qty<min/2?"critical":"low"):"ok";


const Bdg = ({s,type="s",T}) => {
  const cfg = type==="s" ? SC(T)[s] : PC(T)[s];
  if (!cfg) return null;
  return <span style={{padding:"2px 9px",borderRadius:16,fontSize:11,fontWeight:700,color:cfg.c,background:cfg.bg||`${cfg.c}22`,textTransform:"uppercase",letterSpacing:"0.3px"}}>{cfg.lbl}</span>;
};
const Av = ({init,sz=36,c,T}) => (
  <div style={{width:sz,height:sz,borderRadius:"50%",background:`${c||T.accent}22`,border:`1.5px solid ${c||T.accent}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:sz*.32,fontWeight:800,color:c||T.accent,flexShrink:0}}>{init}</div>
);
const StatCard = ({label,value,sub,icon,color,trend,T}) => (
  <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:"20px 22px",display:"flex",flexDirection:"column",gap:14,position:"relative",overflow:"hidden"}}>
    <div style={{position:"absolute",top:-20,right:-20,width:90,height:90,borderRadius:"50%",background:`${color}08`}}/>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
      <div style={{background:`${color}22`,borderRadius:10,padding:10,fontSize:18,lineHeight:1}}>{icon}</div>
      {trend!=null&&<div style={{fontSize:12,fontWeight:700,color:trend>0?T.success:T.danger}}>{trend>0?"↑":"↓"}{Math.abs(trend)}%</div>}
    </div>
    <div>
      <div style={{fontSize:30,fontWeight:800,color:T.text,letterSpacing:"-1px",lineHeight:1}}>{value}</div>
      <div style={{fontSize:13,color:T.muted,marginTop:4}}>{label}</div>
      {sub&&<div style={{fontSize:12,color,fontWeight:600,marginTop:3}}>{sub}</div>}
    </div>
  </div>
);

const BtnPrimary = ({onClick, children, T}) => (
  <button onClick={onClick} style={{background:T.accent,border:"none",borderRadius:9,padding:"10px 18px",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:7,whiteSpace:"nowrap"}}>
    {children}
  </button>
);


// Hook responsive
function useResponsive() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(()=>{
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener('resize', fn);
    fn();
    return () => window.removeEventListener('resize', fn);
  },[]);
  return { isMobile: width <= 768, isTablet: width <= 1024 && width > 768, width };
}

function useSystemTheme() {
  const getDark = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const [dark, setDark] = useState(getDark);
  useEffect(()=>{
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const fn = e => { setDark(e.matches); };
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  },[]);
  return dark;
}



function NavMenu({navItems,view,setView,sidebarCollapsed,collapsedGroups,toggleGroup,tickets,stockAlerts,T}) {
    const ICONS = {
    pipeline:   ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="4" height="18" rx="1"/><rect x="10" y="3" width="4" height="12" rx="1"/><rect x="17" y="3" width="4" height="15" rx="1"/></svg>,
    dashboard: ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    carte:      ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
    interventions:()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
    planning:   ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    sav:        ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    stock:      ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
    vehicules:  ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 4v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
    clients:    ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    stats:      ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    modeles:    ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    taches:     ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
    "parc-it":  ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
    settings:   ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  };
  const sections = [];
  let cur = null;
  navItems.forEach(n => {
    if (n.group === "header") { cur = {h:n, items:[]}; sections.push(cur); }
    else if (!n.group) { sections.push({h:null, items:[n]}); cur = null; }
    else if (cur) cur.items.push(n);
  });
  const NavBtn = ({n}) => {
    const active = view === n.id;
    const badge = n.id==="sav" ? tickets.filter(t=>["ouvert","open"].includes(t.statut)).length : 0;
    const Icon = ICONS[n.id] || (() => <span style={{fontSize:14}}>{n.ic}</span>);
    return (
      <button onClick={()=>setView(n.id)}
        style={{
          display:"flex",alignItems:"center",gap:11,
          padding:sidebarCollapsed?"10px 0":"9px 12px",
          borderRadius:8,border:"none",
          background:active?T.accentDim:"transparent",
          color:active?T.accent:T.muted,
          fontSize:13,fontWeight:active?600:400,
          cursor:"pointer",width:"100%",
          justifyContent:sidebarCollapsed?"center":"flex-start",
          position:"relative",
          transition:"background 0.2s ease, color 0.2s ease, transform 0.2s cubic-bezier(0.34,1.56,0.64,1)",
          fontFamily:"'Inter','Outfit',sans-serif",
        }}
        onMouseEnter={e=>{if(!active){e.currentTarget.style.background=T.accentDim;e.currentTarget.style.transform="translateX(3px)";e.currentTarget.style.color=T.accent;}}}
        onMouseLeave={e=>{if(!active){e.currentTarget.style.background="transparent";e.currentTarget.style.transform="translateX(0)";e.currentTarget.style.color=T.muted;}}}>
        {active&&!sidebarCollapsed&&<div style={{position:"absolute",left:0,top:"20%",bottom:"20%",width:3,borderRadius:2,background:T.accent}}/>}
        <span style={{color:active?T.accent:T.muted,flexShrink:0,display:"flex",transition:"color 0.15s"}}><Icon/></span>
        {!sidebarCollapsed&&<>
          <span style={{flex:1,textAlign:"left",letterSpacing:"0.01em"}}>{n.lbl}</span>
          {badge>0&&<span style={{background:"#ef4444",color:"#fff",borderRadius:12,padding:"2px 7px",fontSize:10,fontWeight:700,lineHeight:1.4}}>{badge}</span>}
          {n.id==="stock"&&stockAlerts>0&&<span style={{width:7,height:7,borderRadius:"50%",background:"#f59e0b",display:"inline-block",marginRight:2}}/>}
        </>}
      </button>
    );
  };
  return (
    <>

      {sections.map((sec,si) => {
        if (!sec.h) return sec.items.map(n => <NavBtn key={n.id} n={n}/>);
        const gKey = sec.h.id.replace("__","");
        const isCol = !!collapsedGroups[gKey];
        const hasAct = sec.items.some(n=>view===n.id);
        return (
          <div key={sec.h.id} style={{marginTop:6}}>
            {!sidebarCollapsed&&(
              <div onClick={()=>toggleGroup(gKey)} role="button" tabIndex={0}
                onKeyDown={e=>(e.key==="Enter"||e.key===" ")&&toggleGroup(gKey)}
                style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 12px",cursor:"pointer",userSelect:"none"}}>
                <span style={{fontSize:9,fontWeight:700,color:hasAct?T.accent:T.muted,textTransform:"uppercase",letterSpacing:"1.4px",fontFamily:"'Inter',sans-serif"}}>{sec.h.lbl}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={hasAct?"#94a3b8":"#cbd5e1"} strokeWidth="2.5"
                  style={{transform:isCol?"rotate(-90deg)":"rotate(0deg)",transition:"transform 0.25s cubic-bezier(0.4,0,0.2,1)"}}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            )}
            <div style={{overflow:"hidden",maxHeight:isCol?"0":"500px",opacity:isCol?0:1,transition:"max-height 0.3s cubic-bezier(0.4,0,0.2,1),opacity 0.2s ease"}}>
              {sec.items.map(n => <NavBtn key={n.id} n={n}/>)}
            </div>
          </div>
        );
      })}
    </>
  );
}


export default function App() {
  const getInitialThemeId = () => {
    try {
      const u = JSON.parse(localStorage.getItem("nexo_user"));
      if (u?.theme_id) return u.theme_id;
    } catch {}
    const saved = localStorage.getItem("nexo_theme_id");
    if (saved) return saved;
    const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isDark ? "dark_slate" : "gsgroup";
  };
  const { isMobile, isTablet } = useResponsive();
  const systemDark = useSystemTheme();
  const [themeId, setThemeId] = useState(getInitialThemeId);
  const [isDark, setIsDark]   = useState(true);
  const T = getTheme(themeId);
  useEffect(()=>{
    const saved = localStorage.getItem("nexo_theme_id");
    if (!saved) setThemeId(systemDark ? "dark_slate" : "light_clean");
  },[systemDark]);

  // Raccourci Ctrl+K pour la recherche
  useEffect(()=>{
    const fn = e => { if((e.ctrlKey||e.metaKey)&&e.key==="k"){ e.preventDefault(); setShowSearch(s=>!s); } };
    document.addEventListener("keydown",fn);
    return ()=>document.removeEventListener("keydown",fn);
  },[]);

  // Polling notifications
  useEffect(()=>{
    const fetchUnread = async () => {
      try {
        const d = await fetch('https://support.gs-group.fr/api/notifications/unread',{headers:{'Authorization':'Bearer '+localStorage.getItem('nexo_token')}}).then(r=>r.json());
        if (d.count !== undefined) setUnreadCount(d.count);
      } catch(e){}
    };
    if (user) { fetchUnread(); const t = setInterval(fetchUnread,30000); return ()=>clearInterval(t); }
  },[user]);

  const toggleTheme = () => {
    const isDarkNow = T.dark;
    const newId = isDarkNow ? "light_clean" : "dark_slate";
    setThemeId(newId);
    localStorage.setItem("nexo_theme_id", newId);
  };

  // Écouter les changements de mode système
  useState(()=>{
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const saved = localStorage.getItem("nexo_theme_id");
      if (!saved) setThemeId(e.matches ? "dark_slate" : "light_clean");
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  });

  const [user, setUser] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("nexo_user"));
      if (u) {
        // Enrichir depuis localStorage si absent
        if (!u.avatar_url) u.avatar_url = localStorage.getItem('nexo_avatar_url')||'';
        if (!u.theme_id)   u.theme_id   = localStorage.getItem('nexo_theme_id')||null;
        if (!u.avatar_color) u.avatar_color = localStorage.getItem('nexo_avatar_color')||'#5b6ef8';
      }
      return u;
    } catch { return null; }
  });
  const [view, setView] = useState("dashboard");
  const [interventions, setInterventions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [stock, setStock] = useState([]);
  const [stockRecent, setStockRecent] = useState([]);
  const [vehiculesGlobal, setVehiculesGlobal] = useState([]);
  const [pendingVehiculeId, setPendingVehiculeId] = useState(null);
  const [escaladeNotifs, setEscaladeNotifs] = useState([]);
  const addEscalade = (notif) => setEscaladeNotifs(p=>[...p.filter(n=>n.id!==notif.id), notif]);
  const dismissEscalade = (id) => setEscaladeNotifs(p=>p.filter(n=>n.id!==id));
  const [clients, setClients] = useState([]);
  const [techniciens, setTechniciens] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showInvitation, setShowInvitation] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mfaUserId, setMfaUserId] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isTablet);
  useEffect(()=>{ setSidebarCollapsed(isTablet); },[isTablet]);
  const [exportPDF, setExportPDF]     = useState(null);
  const [showFicheConsigne, setShowFicheConsigne] = useState(null);
  const [showChecklist, setShowChecklist] = useState(null);
  const [showNonConf, setShowNonConf] = useState(null);
  const [echecAlerts, setEchecAlerts] = useState([]); // alertes d'interventions échouées (SSE)
  const [showZonesCameras, setShowZonesCameras] = useState(null);
  const [suggGeo, setSuggGeo]         = useState(null);
  const [assignTicket, setAssignTicket] = useState(null);
  const [pvInt, setPvInt] = useState(null);
  const [ficheConsigneInt, setFicheConsigneInt] = useState(null);
  const [techInt, setTechInt] = useState(null);
  const [editClient, setEditClient] = useState(null);

  // Modals
  const [showInt,    setShowInt]    = useState(false);
  const [showClient, setShowClient] = useState(false);
  const [showSAV,    setShowSAV]    = useState(false);
  const [showStock,  setShowStock]  = useState(false);
  const [selInt,     setSelInt]     = useState(null);

  // Vérifier si l'utilisateur a accès à un module
  const canAccess = (id) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const mods = user?.preferences?.modules;
    if (!mods) return true; // pas de restrictions définies → tout visible
    return mods[id] !== false;
  };

  const ALL_NAV_ADMIN = [
    // Principaux
    {id:"dashboard",    lbl:"Dashboard",      ic:"🏠",  group:null},
    {id:"pipeline",     lbl:"Pipeline",       ic:"📋",  group:null},
    {id:"taches",       lbl:"Tâches",         ic:"✅",  group:null},
    {id:"planning",     lbl:"Planning",       ic:"📅",  group:null},
    {id:"carte",        lbl:"Carte",          ic:"🗺️", group:null},
    {id:"parc-it",      lbl:"Parc IT",        ic:"🖥️", group:null},
    // Ressources
    {id:"__res",        lbl:"Ressources",     ic:"",    group:"header"},
    {id:"clients",      lbl:"Clients",        ic:"👥",  group:"res"},
    {id:"stock",        lbl:"Stock",          ic:"📦",  group:"res"},
    {id:"vehicules",    lbl:"Flotte",         ic:"🚗",  group:"res"},
    // Outils
    {id:"__outils",     lbl:"Outils",         ic:"",    group:"header"},
    {id:"stats",        lbl:"Statistiques",   ic:"📊",  group:"outils"},
    {id:"heures",       lbl:"Heures",         ic:"⏱️",  group:"outils"},
    {id:"settings",     lbl:"Paramètres",     ic:"⚙️", group:"outils"},
  ];
  // Filtrer selon les permissions (les headers sont conservés)
  const navItemsAdmin = ALL_NAV_ADMIN.filter(n => n.group === "header" || canAccess(n.id));

  const navItemsTech = [
    {id:"portail",      lbl:"Mon Espace",     ic:"🏠"},
    {id:"pipeline",     lbl:"Pipeline",       ic:"📋"},
    {id:"planning",     lbl:"Planning",       ic:"📅"},
    {id:"carte",        lbl:"Carte",          ic:"🗺️"},
    {id:"vehicules",    lbl:"Mon véhicule",   ic:"🚗"},
  ];
  const navItems = user?.role==="technicien" ? navItemsTech : navItemsAdmin;

  const { confirm, Dialog: ConfirmDialog } = useConfirm();
  const [vehiculeAlertes, setVehiculeAlertes] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState({avance: true});
  const navRef = useRef(null);

  // Effet liquid glass sur la nav
  useEffect(() => {
    const nav = document.querySelector('.app-sidebar nav');
    if (!nav) return;
    const pill = document.createElement('div');
    pill.id = 'nav-pill';
    pill.style.cssText = 'position:absolute;left:4px;right:4px;border-radius:10px;background:linear-gradient(135deg,rgba(67,97,238,0.10),rgba(123,97,255,0.07));backdrop-filter:blur(6px);border:1px solid rgba(67,97,238,0.12);transition:top 0.22s cubic-bezier(0.34,1.56,0.64,1),height 0.15s ease,opacity 0.2s ease;pointer-events:none;opacity:0;z-index:0;';
    nav.style.position = 'relative';
    nav.appendChild(pill);
    let active = false;
    const show = (el) => {
      const nr = nav.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      pill.style.top = (er.top - nr.top + nav.scrollTop) + 'px';
      pill.style.height = er.height + 'px';
      pill.style.opacity = '1';
    };
    const hide = () => { pill.style.opacity = '0'; };
    const onDown = (e) => { const b = e.target.closest('button[data-nav]'); if(b){active=true;show(b);} };
    const onMove = (e) => { if(!active)return; const b=e.target.closest('button[data-nav]'); if(b)show(b); };
    const onUp = (e) => { if(!active)return; active=false; setTimeout(hide,300); };
    const onOver = (e) => { const b=e.target.closest('button[data-nav]'); if(b)show(b); };
    const onLeave = () => { if(!active)hide(); };
    nav.addEventListener('mousedown',onDown);
    nav.addEventListener('mousemove',onMove);
    window.addEventListener('mouseup',onUp);
    nav.addEventListener('mouseover',onOver);
    nav.addEventListener('mouseleave',onLeave);
    return () => {
      nav.removeEventListener('mousedown',onDown);
      nav.removeEventListener('mousemove',onMove);
      window.removeEventListener('mouseup',onUp);
      nav.removeEventListener('mouseover',onOver);
      nav.removeEventListener('mouseleave',onLeave);
      pill.remove();
    };
  }, [view]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selTicket, setSelTicket] = useState(null);
  const [tkClose, setTkClose] = useState(null); // {email,msg,sendMail,saving,newStatut,emailError,typeResolution,interventionId}
  const toggleGroup = (g) => setCollapsedGroups(p=>({...p,[g]:!p[g]}));


  // ── Animations CSS globales (injectées une fois) ──────────────
  
  const loadAll = async () => {
    setLoading(true);
    try {
      const [iv,tk,st,cl,tc,vh] = await Promise.all([
        api.getInterventions(), api.getTickets(), api.getStock(), api.getClients(), api.getTechniciens(),
        fetch('https://support.gs-group.fr/api/vehicules',{headers:{'Authorization':'Bearer '+localStorage.getItem('nexo_token')}}).then(r=>r.json()).catch(()=>[])
      ]);
      setTechniciens(Array.isArray(tc)?tc:[]);
      setInterventions(Array.isArray(iv)?iv:[]);
      setTickets(Array.isArray(tk)?tk:[]);
      setStock(Array.isArray(st)?st:[]);
      setClients(Array.isArray(cl)?cl:[]);
      setVehiculesGlobal(Array.isArray(vh)?vh:[]);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { if(user) loadAll(); }, [user]);

  // ══ SSE + POLLING TEMPS RÉEL ══
  useEffect(() => {
    if (!user) return;

    const loadAlertes = async () => {
      try {
        const resp = await fetch("https://support.gs-group.fr/api/vehicule-alertes",{headers:{"Authorization":"Bearer "+localStorage.getItem("nexo_token")}});
        if(resp.ok){ const data=await resp.json(); setVehiculeAlertes(Array.isArray(data)?data:[]); }
      } catch(e) {}
    };
    loadAlertes();
    const alertInterval = setInterval(loadAlertes, 60000);

    // SSE — push instantané dès qu'un statut change
    const token = localStorage.getItem("nexo_token");
    let es = null;
    let sseOk = false;
    const connectSSE = () => {
      try {
        es = new EventSource(`https://support.gs-group.fr/api/events?token=${token}`);
        es.addEventListener("intervention_updated", () => {
          sseOk = true;
          api.getInterventions().then(iv=>{ if(Array.isArray(iv)) setInterventions(iv); }).catch(()=>{});
        });
        es.addEventListener("ticket_updated", () => {
          sseOk = true;
          api.getTickets().then(tk=>{ if(Array.isArray(tk)) setTickets(tk); }).catch(()=>{});
        });
        es.addEventListener("intervention_echec", (e) => {
          sseOk = true;
          try {
            const data = JSON.parse(e.data);
            setEchecAlerts(prev => [...prev.filter(a=>a.id!==data.id), data]);
            api.getInterventions().then(iv=>{ if(Array.isArray(iv)) setInterventions(iv); }).catch(()=>{});
          } catch(err) {}
        });
        es.onerror = () => { es.close(); setTimeout(connectSSE, 5000); };
      } catch(e) {}
    };
    connectSSE();

    // Polling de secours toutes les 15s (couvre les cas hors SSE)
    const poll = setInterval(async () => {
      try {
        const [tk, iv] = await Promise.all([api.getTickets(), api.getInterventions()]);
        setTickets(Array.isArray(tk)?tk:[]);
        setInterventions(Array.isArray(iv)?iv:[]);
      } catch(e) {}
    }, 15000);

    return () => {
      clearInterval(poll);
      clearInterval(alertInterval);
      if(es) es.close();
    };
  }, [user]);

  const logout = () => { localStorage.removeItem("nexo_token"); localStorage.removeItem("nexo_user"); setUser(null); };

  if (window.location.pathname==='/set-password' || window.location.search.includes('token=')) {
    return <SetPassword/>;
  }
  if (isMobile && user) return <MobileApp user={user} interventions={interventions} tickets={tickets} T={T} onLogout={logout} onSelectInt={i=>{setSelInt(i);}} onUpdated={async()=>{
    const [s,r] = await Promise.all([
      api.getStock(),
      fetch("https://support.gs-group.fr/api/stock/recent",{headers:{"Authorization":"Bearer "+localStorage.getItem("nexo_token")}}).then(x=>x.json()).catch(()=>[])
    ]);
    setStock(Array.isArray(s)?s:[]);
    setStockRecent(Array.isArray(r)?r:[]);
  }}/>
  if (!user) return <Login onLogin={u=>setUser(u)} T={T} isDark={isDark} toggleTheme={toggleTheme}/>;

  const openSAV    = tickets.filter(t=>t.statut==="open").length;
  const stockAlerts = stock.filter(s=>getStockStatus(s.quantite,s.quantite_min)!=="ok").length;
  const activeInts  = interventions.filter(i=>i.statut==="in-progress").length;
  const pendingInts = interventions.filter(i=>i.statut==="pending").length;

  const TECH_ALLOWED_VIEWS = new Set(["portail","dashboard","pipeline","planning","carte","vehicules"]);

  const renderView = () => {
    if (loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:300,color:T.muted}}>⏳ Chargement...</div>;

    // Guard rôle technicien — accès restreint
    if (user?.role === "technicien" && !TECH_ALLOWED_VIEWS.has(view)) {
      setTimeout(() => setView("portail"), 0);
      return null;
    }
    // Redirect technicien away from dashboard to portal
    if (user?.role === "technicien" && view === "dashboard") {
      setTimeout(() => setView("portail"), 0);
      return null;
    }

    if(view==="stats") return <Stats T={T}/>;
    if(view==="planning") return <Planning T={T} interventions={interventions} tickets={tickets} techniciens={techniciens} onSelectInt={i=>setSelInt(i)} onNewInt={()=>setShowInt(true)} currentUser={user}/>;
    if (view==="carte") return (
      <MapView T={T} interventions={interventions} tickets={tickets} techniciens={techniciens} currentUser={user}/>
    );

    if (view==="portail") return (
      <PortailTechnicien T={T} user={user} onOpenPV={i=>{setTechInt(i);}}/>
    );

    if (view==="dashboard" && user.role==="technicien") return (
      <DashboardTechnicien T={T} user={user} onOpenPV={i=>{setTechInt(i);}}/>
    );
    if (view==="dashboard") return (
      <div style={{display:"flex",flexDirection:"column",gap:20}}>
        <DashboardNew T={T} interventions={interventions} tickets={tickets} techniciens={techniciens} stock={stock}
          user={user} setView={setView} setSelInt={setSelInt} onNewInt={()=>setShowInt(true)} onNewSAV={()=>setShowSAV(true)}
          echecAlerts={echecAlerts} onClearEchec={id=>setEchecAlerts(prev=>prev.filter(a=>a.id!==id))}/>
        <WarningsPanel T={T} onSelectInt={i=>setSelInt(i)} onEscalade={addEscalade}/>
      </div>
    );

    if (view==="interventions") return <IntListView T={T} interventions={interventions} onSelect={i=>setSelInt(i)} onNew={()=>setShowInt(true)}/>;
    if (view==="taches") return <TodoView T={T} currentUser={user} tickets={tickets} interventions={interventions} clients={clients} techniciens={techniciens}/>;

    if (view==="pipeline") return (
      <Pipeline T={T} tickets={tickets} interventions={interventions} clients={clients} techniciens={techniciens}
        onUpdated={loadAll}
        onSelectTicket={t=>setSelTicket(t)}
        onSelectInt={i=>setSelInt(i)}
        onNewTicket={()=>setShowSAV(true)}
        onNewInt={()=>setShowInt(true)}
        echecAlerts={echecAlerts}/>
    );
    if (view==="sav") return (
      <SAVListView T={T} tickets={tickets} clients={clients} techniciens={techniciens}
        onNew={()=>setShowSAV(true)}
        onUpdated={loadAll}
      />
    );
     if (view==="stock") return (
      <StockView
        T={T}
        stock={stock}
        onRefresh={async()=>{ const s=await api.getStock(); setStock(Array.isArray(s)?s:[]); }}
        onUpdate={async(id,data)=>{
          await fetch("https://support.gs-group.fr/api/stock/"+id,{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+localStorage.getItem("nexo_token")},body:JSON.stringify(data)});
          const s=await api.getStock(); setStock(s);
        }}
        onDelete={async(id)=>{
          await fetch("https://support.gs-group.fr/api/stock/"+id,{method:"DELETE",headers:{"Authorization":"Bearer "+localStorage.getItem("nexo_token")}});
          const s=await api.getStock(); setStock(s);
        }}
        onResetAll={async()=>{
          if(!await confirm("Remettre toutes les quantités à 0 ?","Cela affecte les "+stock.length+" produits.")) return;
          await fetch("https://support.gs-group.fr/api/stock/reset-quantities",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+localStorage.getItem("nexo_token")}});
          const s=await api.getStock(); setStock(s);
        }}
        onDeleteAll={async()=>{
          if(!await confirm("Supprimer TOUS les produits ?","Cette action est irréversible et supprime "+stock.length+" produits.")) return;
          await fetch("https://support.gs-group.fr/api/stock/delete-all",{method:"DELETE",headers:{"Content-Type":"application/json","Authorization":"Bearer "+localStorage.getItem("nexo_token")}});
          const s=await api.getStock(); setStock(s);
        }}
        onAdd={()=>setShowStock(true)}
      />
    );


    if (view==="clients") return (
      <div style={{display:"flex",flexDirection:"column",gap:22}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:isMobile?18:25,fontWeight:800,color:T.text,letterSpacing:"-0.7px"}}>Clients</div>
            <div style={{fontSize:13,color:T.muted,marginTop:4}}>{clients.length} client(s)</div>
          </div>
          <BtnPrimary onClick={()=>setShowClient(true)} T={T}>＋ Nouveau client</BtnPrimary>
        </div>
        {clients.length===0
          ? <div style={{textAlign:"center",padding:60,color:T.muted}}>Aucun client enregistré</div>
          : <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
              {clients.map(c=>(
                <div key={c.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:13,padding:"16px 18px",display:"flex",gap:13,alignItems:"flex-start"}}>
                  <Av init={(c.nom||"?")[0].toUpperCase()} sz={40} T={T}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:700,color:T.text}}>{c.nom}</div>
                    <div style={{fontSize:12,color:T.muted,marginTop:3}}>📧 {c.email||"—"}</div>
                    <div style={{fontSize:12,color:T.muted}}>📞 {c.telephone||"—"}</div>
                    <div style={{fontSize:12,color:T.muted}}>📍 {c.adresse?`${c.adresse}, `:""}{c.ville||"—"} {c.code_postal||""}</div>
                    {c.lat&&<div style={{fontSize:11,color:T.success,marginTop:2}}>✅ Géolocalisé</div>}
                  </div>
                  <button onClick={()=>setEditClient(c)} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.accent}40`,background:T.accentDim,color:T.accent,fontSize:11,fontWeight:600,cursor:"pointer",flexShrink:0}}>✏️ Modifier</button>
                </div>
              ))}
            </div>
        }
      </div>
    );

    if (view==="vehicules") return <ParcsVehicules T={T} currentUser={user} techniciens={techniciens} openVehiculeId={pendingVehiculeId} onOpenHandled={()=>setPendingVehiculeId(null)}/>;
    if (view==="heures")    return <HeuresView T={T} user={user} techniciens={techniciens} interventions={interventions}/>;
    if (view==="parc-it") return <ParcIT T={T}/>;
    if (view==="settings")  return <SettingsView T={T} currentUser={user} toggleTheme={toggleTheme} isDark={isDark} onInvite={()=>setShowInvitation(true)}/>;
    if (view==="users")     return <Users T={T}/>;
    // legacy redirects — modeles/automations/users-mgmt are now in Paramètres
    if (view==="modeles" || view==="automations" || view==="users-mgmt") { setView("settings"); return null; }

    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:400,gap:14}}>
        <div style={{fontSize:52}}>🚧</div>
        <div style={{fontSize:20,fontWeight:700,color:T.text}}>{navItems.find(n=>n.id===view)?.lbl}</div>
        <div style={{fontSize:13,color:T.muted}}>Module en cours de développement</div>
      </div>
    );
  };

  return (
    <>
      <div className="app-layout" style={{background:T.bg,color:T.text,overflow:"hidden",background:T.bg,color:T.text,overflow:"hidden",transition:"all 0.4s",position:"relative"}}>

        {/* SIDEBAR */}
        <aside className="app-sidebar" style={{background:T.sidebar,borderRight:`1px solid ${T.border}`,width:sidebarCollapsed?64:225,flexShrink:0,transition:"width 0.25s cubic-bezier(0.4,0,0.2,1)",overflow:"hidden",position:"relative",zIndex:10}}>
          {/* Logo */}
          <div style={{padding:"16px 14px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:10,minHeight:64}}>
            <Logo variant="sidebar-dark" collapsed={sidebarCollapsed}/>
            {!sidebarCollapsed&&<div style={{minWidth:0,overflow:"hidden"}}>
              <div style={{fontSize:12,fontWeight:800,color:T.text,whiteSpace:"nowrap"}}>Support Technique</div>
              <div style={{fontSize:10,color:T.accent,fontWeight:700,letterSpacing:"1px",textTransform:"uppercase"}}>GS-GROUP</div>
            </div>}
          </div>

          {/* Nav */}
          <nav style={{flex:1,padding:"8px 6px",display:"flex",flexDirection:"column",gap:1,overflowY:"auto",overflowX:"hidden",position:"relative"}}>
            <NavMenu navItems={navItems} view={view} setView={setView} sidebarCollapsed={sidebarCollapsed}
              collapsedGroups={collapsedGroups} toggleGroup={toggleGroup}
              tickets={tickets} stockAlerts={stockAlerts} T={T}/>
          </nav>


          <div style={{padding:"10px 12px",borderTop:`1px solid ${T.border}`}}>
            {!sidebarCollapsed&&<div style={{fontSize:9,color:T.muted,textAlign:"center",marginBottom:8,fontFamily:"'Inter',sans-serif",letterSpacing:"0.3px",opacity:0.6}}>Développé par Lucas Egly · GS-GROUP 2026</div>}
            <button onClick={()=>setSidebarCollapsed(p=>!p)}
              style={{width:"100%",padding:"8px",borderRadius:8,border:"1px solid rgba(255,255,255,0.1)",background:"transparent",color:"#64748b",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}
              onMouseEnter={e=>{e.currentTarget.style.background=T.bg;e.currentTarget.style.color=T.text;}}
              onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color="#94a3b8";}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{transform:sidebarCollapsed?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.3s cubic-bezier(0.4,0,0.2,1)"}}>
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              {!sidebarCollapsed&&<span style={{fontSize:12,marginLeft:6,fontFamily:"'Inter',sans-serif"}}>Réduire</span>}
            </button>
          </div>
        </aside>

        {/* CONTENU PRINCIPAL */}
        <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0,overflow:"hidden"}}>
          <div style={{height:52,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 20px",background:T.card,flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <Logo variant="header-light" fallback="none"/>
              <div style={{fontSize:18,fontWeight:700,color:T.text}}>{navItems.find(n=>n.id===view)?.lbl||""}</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <button onClick={()=>setShowSearch(true)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:T.muted}}>🔍</button>
              <div style={{position:"relative"}}>
              <button onClick={()=>setShowNotif(!showNotif)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:vehiculeAlertes.filter(a=>!a.vu).length>0?"#ef4444":T.muted}}>🔔</button>
              {vehiculeAlertes.filter(a=>!a.vu).length>0&&(
                <span style={{position:"absolute",top:-4,right:-4,background:"#ef4444",color:"#fff",borderRadius:"50%",width:16,height:16,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700}}>
                  {vehiculeAlertes.filter(a=>!a.vu).length}
                </span>
              )}
            </div>
              <button onClick={toggleTheme} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:T.muted}}>{isDark?"☀️":"🌙"}</button>
              <div style={{position:"relative"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"4px 10px",borderRadius:20,background:T.card,border:`1px solid ${T.border}`,cursor:"pointer",transition:"all 0.15s"}}
                  onClick={()=>setShowUserMenu(p=>!p)}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.accent;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:user?.avatar_color||T.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff",flexShrink:0}}>
                    {(user?.prenom?.[0]||user?.email?.[0]||"?").toUpperCase()}
                  </div>
                  <div style={{fontSize:12,fontWeight:600,color:T.text}}>{user?.prenom||user?.email?.split("@")[0]}</div>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{color:T.muted,transform:showUserMenu?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s"}}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
                {showUserMenu&&<>
                  <div style={{position:"fixed",inset:0,zIndex:998}} onClick={()=>setShowUserMenu(false)}/>
                  <div style={{position:"absolute",right:0,top:"calc(100% + 8px)",zIndex:999,
                    background:T.card,border:`1px solid ${T.border}`,borderRadius:14,
                    boxShadow:"0 8px 32px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.08)",
                    padding:8,minWidth:200,backdropFilter:"blur(12px)",
                    animation:"dropIn 0.22s cubic-bezier(0.16,1,0.3,1) both"}}>
                    <style>{`@keyframes dropIn{from{opacity:0;transform:translateY(-8px) scale(0.96)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
                    {/* Infos utilisateur */}
                    <div style={{padding:"10px 12px",borderBottom:`1px solid ${T.border}`,marginBottom:4}}>
                      <div style={{fontSize:13,fontWeight:700,color:T.text}}>{user?.prenom} {user?.nom||""}</div>
                      <div style={{fontSize:11,color:T.muted,marginTop:2}}>{user?.email}</div>
                      <div style={{fontSize:10,color:T.accent,fontWeight:600,marginTop:3,textTransform:"uppercase",letterSpacing:"0.5px"}}>{user?.role}</div>
                    </div>
                    {/* Options */}
                    {[
                      {ic:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>, lbl:"Mon profil", action:()=>{setShowProfile(true);setShowUserMenu(false);}},
                      {ic:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06-.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>, lbl:"Paramètres", action:()=>{setView("settings");setShowUserMenu(false);}},
                    ].map(item=>(
                      <button key={item.lbl} onClick={item.action}
                        style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 12px",borderRadius:8,border:"none",background:"transparent",color:T.text,fontSize:13,cursor:"pointer",textAlign:"left",transition:"background 0.12s"}}
                        onMouseEnter={e=>e.currentTarget.style.background=T.bg}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span style={{color:T.muted}}>{item.ic}</span>{item.lbl}
                      </button>
                    ))}
                    <div style={{borderTop:`1px solid ${T.border}`,marginTop:4,paddingTop:4}}>
                      <button onClick={()=>{setShowUserMenu(false);logout();}}
                        style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 12px",borderRadius:8,border:"none",background:"transparent",color:"#ef4444",fontSize:13,cursor:"pointer",textAlign:"left",transition:"background 0.12s"}}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(239,68,68,0.08)"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                </>}
              </div>

            </div>
          </div>
          <div style={{flex:1,padding:"20px 24px",overflowY:"auto"}}>
            {renderView()}
          </div>
        </div>
      </div>

      {selTicket&&(()=>{
        const tk = selTicket;
        const sc = {"ouvert":"#ef4444","open":"#ef4444","en_cours":"#f59e0b","in-progress":"#f59e0b","resolu":"#10b981","resolved":"#10b981","ferme":"#94a3b8","closed":"#94a3b8"}[tk.statut]||"#94a3b8";
        const sl = {"ouvert":"Ouvert","open":"Ouvert","en_cours":"En cours","in-progress":"En cours","resolu":"Résolu","resolved":"Résolu","ferme":"Fermé","closed":"Fermé"}[tk.statut]||tk.statut;
        const openEmailClose = (newStatut) => {
          const email = tk.client_email||"";
          const lbl = newStatut==="resolu"?"résolu":"fermé";
          const ref = tk.reference?.startsWith("SAV-") ? " ("+tk.reference+")" : "";
          const msg = `Bonjour${tk.client_nom?" "+tk.client_nom:""},\n\nVotre ticket SAV${ref} concernant "${tk.objet}" a été ${lbl}.\n\nSi le problème persiste, n'hésitez pas à nous recontacter.\n\nCordialement,\nL'équipe GS GROUP\nTél : 03.89.50.70.80 · contact@gs-group.fr`;
          setTkClose({newStatut, email, msg, sendMail:!!email, saving:false, typeResolution:"distance", interventionId:tk.intervention_id||null});
        };
        const doClose = async (forceSend) => {
          const send = forceSend!==undefined ? forceSend : tkClose.sendMail;
          const snapClose = tkClose;
          setTkClose(p=>({...p,saving:true,emailError:null}));
          const patchBody = {statut:snapClose.newStatut};
          if(snapClose.interventionId) patchBody.intervention_id = snapClose.interventionId;
          await fetch(`https://support.gs-group.fr/api/tickets/${tk.id}`,{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+localStorage.getItem("nexo_token")},body:JSON.stringify(patchBody)});
          if(send && snapClose.email){
            const lbl = snapClose.newStatut==="resolu"?"résolu":"fermé";
            const ref = tk.reference?.startsWith("SAV-")?" ("+tk.reference+")":"";
            try {
              const r = await fetch(`https://support.gs-group.fr/api/tickets/${tk.id}/send-closure-email`,{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+localStorage.getItem("nexo_token")},body:JSON.stringify({to:snapClose.email,subject:`Votre ticket SAV${ref} est ${lbl} — GS GROUP`,body:snapClose.msg})});
              if(!r.ok){ const d=await r.json().catch(()=>({})); setTkClose(p=>({...p,saving:false,emailError:"Erreur envoi email: "+(d.error||r.status)})); loadAll(); return; }
            } catch(e){ setTkClose(p=>({...p,saving:false,emailError:"Erreur réseau: "+e.message})); loadAll(); return; }
          }
          setTkClose(null); setSelTicket(null); loadAll();
        };
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(8,12,30,0.28)",backdropFilter:"blur(4px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>{if(!tkClose)setSelTicket(null);}}>
            <div onClick={e=>e.stopPropagation()} style={{background:T.card,borderRadius:16,width:"min(560px,96vw)",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 80px rgba(0,0,0,0.2)",animation:"nx-modal-open 0.3s cubic-bezier(0.16,1,0.3,1) both",border:`1px solid ${T.border}`}}>
              {/* Header */}
              <div style={{padding:"18px 22px 14px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexShrink:0}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:5,flexWrap:"wrap"}}>
                    {tk.reference?.startsWith("SAV-")&&<span style={{fontSize:10,fontFamily:"monospace",color:T.muted,background:T.bg,padding:"2px 8px",borderRadius:20,border:`1px solid ${T.border}`}}>{tk.reference}</span>}
                    <span style={{fontSize:11,fontWeight:700,padding:"2px 9px",borderRadius:20,background:sc+"20",color:sc}}>{sl}</span>
                    {tk.priorite&&<span style={{fontSize:11,fontWeight:700,padding:"2px 9px",borderRadius:20,background:T.bg,color:T.muted,border:`1px solid ${T.border}`}}>{tk.priorite}</span>}
                  </div>
                  <div style={{fontSize:16,fontWeight:800,color:T.text,lineHeight:1.3}}>{tk.objet||tk.titre}</div>
                </div>
                <button onClick={()=>{setSelTicket(null);setTkClose(null);}} style={{background:"none",border:"none",fontSize:20,color:T.muted,cursor:"pointer",padding:4,flexShrink:0}}>✕</button>
              </div>
              {/* Body */}
              <div style={{flex:1,overflowY:"auto",padding:"16px 22px",display:"flex",flexDirection:"column",gap:9}}>
                {[["👤 Client",tk.client_nom],["📧 Email",tk.client_email],["📍 Ville",tk.ville],["⚡ Priorité",tk.priorite],["📅 Créé le",tk.created_at&&new Date(tk.created_at).toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric"})]].filter(([,v])=>v).map(([l,v])=>(
                  <div key={l} style={{display:"flex",gap:10,padding:"9px 13px",background:T.bg,borderRadius:9,border:`1px solid ${T.border}`}}>
                    <span style={{fontSize:12,color:T.muted,flexShrink:0}}>{l}</span>
                    <span style={{fontSize:13,fontWeight:600,color:T.text}}>{v}</span>
                  </div>
                ))}
                {tk.description&&<div style={{padding:"11px 13px",background:T.bg,borderRadius:9,border:`1px solid ${T.border}`}}><div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:6}}>Description</div><div style={{fontSize:13,color:T.text,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{tk.description}</div></div>}
                {tk.intervention_id&&(()=>{
                  const linked = interventions.find(i=>i.id===tk.intervention_id);
                  if(!linked) return null;
                  const stCol = {"terminee":"#10b981","en_cours":"#f59e0b","planifiee":"#8b5cf6","annulee":"#94a3b8"}[linked.statut]||T.muted;
                  return (
                    <div onClick={()=>{setSelTicket(null);setTkClose(null);setSelInt(linked);}} style={{padding:"10px 13px",background:T.bg,borderRadius:9,border:`1.5px solid ${stCol}40`,cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:16}}>🔧</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.5px"}}>Intervention liée</div>
                        <div style={{fontSize:13,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{linked.titre||linked.objet}</div>
                      </div>
                      <span style={{fontSize:11,fontWeight:700,color:stCol,background:stCol+"18",padding:"2px 9px",borderRadius:20,whiteSpace:"nowrap"}}>{linked.statut}</span>
                    </div>
                  );
                })()}

                {/* Formulaire clôture */}
                {tkClose&&(()=>{
                  const tkInts = interventions.filter(i=>!tk.client_id||String(i.client_id)===String(tk.client_id));
                  return (
                  <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 16px",display:"flex",flexDirection:"column",gap:12,marginTop:4}}>
                    <div style={{fontSize:13,fontWeight:800,color:T.text}}>{tkClose.newStatut==="resolu"?"✅ Marquer résolu":"🔒 Fermer le ticket"}</div>

                    {/* Type de résolution */}
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:7}}>Type de résolution</div>
                      <div style={{display:"flex",gap:8}}>
                        {[["distance","🖥️ À distance"],["surplace","🔧 Sur place"]].map(([v,l])=>(
                          <button key={v} onClick={()=>setTkClose(p=>({...p,typeResolution:v}))}
                            style={{flex:1,padding:"9px 8px",borderRadius:9,border:`2px solid ${tkClose.typeResolution===v?T.accent:T.border}`,background:tkClose.typeResolution===v?T.accentDim:"transparent",color:tkClose.typeResolution===v?T.accent:T.muted,fontSize:12,fontWeight:700,cursor:"pointer",transition:"all 0.15s"}}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Lien intervention (sur place) */}
                    {tkClose.typeResolution==="surplace"&&(
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:6}}>🔧 Associer une intervention</div>
                        <select value={tkClose.interventionId||""} onChange={e=>setTkClose(p=>({...p,interventionId:e.target.value||null}))}
                          style={{width:"100%",background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",cursor:"pointer"}}>
                          <option value="">— Sans intervention —</option>
                          {tkInts.map(i=>{
                            const stl = {"planifiee":"📅","en_cours":"⚙️","terminee":"✅","annulee":"❌"}[i.statut]||"🔧";
                            return <option key={i.id} value={i.id}>{stl} {i.titre||i.objet} · {i.statut}</option>;
                          })}
                        </select>
                        {tkInts.length===0&&<div style={{fontSize:11,color:T.muted,marginTop:5}}>Aucune intervention pour ce client — créez-en une depuis le menu Interventions.</div>}
                      </div>
                    )}

                    {/* Toggle email */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 13px",background:T.card,borderRadius:9,border:`1px solid ${T.border}`}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:T.text}}>📧 Envoyer un email au client</div>
                        <div style={{fontSize:11,color:T.muted,marginTop:1}}>Informer que le ticket est {tkClose.newStatut==="resolu"?"résolu":"fermé"}</div>
                      </div>
                      <button onClick={()=>setTkClose(p=>({...p,sendMail:!p.sendMail}))}
                        style={{width:40,height:22,borderRadius:11,border:"none",cursor:"pointer",position:"relative",background:tkClose.sendMail?"#10b981":"#cbd5e1",transition:"background 0.2s",flexShrink:0}}>
                        <div style={{position:"absolute",top:2,left:tkClose.sendMail?18:2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                      </button>
                    </div>

                    {tkClose.sendMail&&(
                      <>
                        <input value={tkClose.email} onChange={e=>setTkClose(p=>({...p,email:e.target.value}))} placeholder="email@client.fr" type="email"
                          style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:13,outline:"none"}}/>
                        {!tkClose.email&&<div style={{fontSize:11,color:"#f59e0b",marginTop:-8}}>⚠ Aucun email renseigné sur la fiche client</div>}
                        <label style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.5px"}}>Message</label>
                        <textarea value={tkClose.msg} onChange={e=>setTkClose(p=>({...p,msg:e.target.value}))} rows={5}
                          style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:12,outline:"none",resize:"vertical",lineHeight:1.6,fontFamily:"inherit"}}/>
                      </>
                    )}

                    {tkClose.emailError&&<div style={{fontSize:12,color:"#ef4444",background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"8px 12px"}}>⚠ {tkClose.emailError}<br/><span style={{fontSize:11,opacity:0.8}}>Le ticket a bien été clôturé, seul l'email n'a pas été envoyé.</span></div>}

                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>doClose(false)} disabled={tkClose.saving}
                        style={{flex:1,padding:"9px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,fontSize:12,fontWeight:600,cursor:"pointer",opacity:tkClose.saving?0.6:1}}>
                        {tkClose.saving?"...":"Sans email"}
                      </button>
                      <button onClick={()=>doClose()} disabled={tkClose.saving||(tkClose.sendMail&&!tkClose.email)}
                        style={{flex:2,padding:"9px",borderRadius:9,border:"none",background:tkClose.sendMail&&tkClose.email?"linear-gradient(135deg,#10b981,#059669)":"linear-gradient(135deg,#64748b,#475569)",color:"#fff",fontSize:12,fontWeight:700,cursor:(tkClose.saving||(tkClose.sendMail&&!tkClose.email))?"not-allowed":"pointer",opacity:tkClose.saving?0.7:1}}>
                        {tkClose.saving?"Traitement...":(tkClose.sendMail&&tkClose.email?"✅ Confirmer + envoyer email":"✅ Confirmer")}
                      </button>
                    </div>
                  </div>
                  );
                })()}
              </div>
              {/* Footer actions */}
              {!tkClose&&(
                <div style={{padding:"13px 22px",borderTop:`1px solid ${T.border}`,display:"flex",gap:8,flexShrink:0,flexWrap:"wrap"}}>
                  {["ouvert","open"].includes(tk.statut)&&(
                    <button onClick={async()=>{await fetch(`https://support.gs-group.fr/api/tickets/${tk.id}`,{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+localStorage.getItem("nexo_token")},body:JSON.stringify({statut:"en_cours"})});setSelTicket(null);loadAll();}}
                      style={{flex:1,padding:"9px",borderRadius:9,background:"#f59e0b20",border:"1px solid #f59e0b40",color:"#f59e0b",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      ▶ Prendre en charge
                    </button>
                  )}
                  {["en_cours","in-progress"].includes(tk.statut)&&(
                    <button onClick={()=>openEmailClose("resolu")}
                      style={{flex:1,padding:"9px",borderRadius:9,background:"#10b98120",border:"1px solid #10b98140",color:"#10b981",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      ✅ Marquer résolu
                    </button>
                  )}
                  {["resolu","resolved"].includes(tk.statut)&&(
                    <button onClick={()=>openEmailClose("ferme")}
                      style={{flex:1,padding:"9px",borderRadius:9,background:"#94a3b820",border:"1px solid #94a3b840",color:"#94a3b8",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      🔒 Fermer le ticket
                    </button>
                  )}
                  {["ouvert","open","en_cours","in-progress"].includes(tk.statut)&&(
                    <button onClick={()=>openEmailClose("ferme")}
                      style={{flex:1,padding:"9px",borderRadius:9,background:"rgba(239,68,68,0.06)",border:"1px solid #ef444440",color:"#ef4444",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      🔒 Fermer direct
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
      <ConfirmDialog/>
      {showInt && <ModalIntervention T={T} clients={clients} techniciens={techniciens} onClose={()=>setShowInt(false)} onCreated={()=>{setShowInt(false);loadAll();}}/>}
      {showSAV && <ModalSAV T={T} clients={clients} techniciens={techniciens} onClose={()=>setShowSAV(false)} onCreated={()=>{setShowSAV(false);loadAll();}}/>}
      {showStock && <ModalStock T={T} clients={clients} onCreated={()=>{setShowStock(false);loadAll();}} onClose={()=>setShowStock(false)}/>}
      {showClient && <ModalClient T={T} onClose={()=>setShowClient(false)} onSaved={()=>{setShowClient(false);loadAll();}}/>}
      {editClient && <ModalClient T={T} client={editClient} onClose={()=>setEditClient(null)} onSaved={()=>{setEditClient(null);loadAll();}}/>}
      {selInt && <IntDetail T={T} intervention={selInt} onClose={()=>setSelInt(null)} onUpdated={(u)=>{setSelInt(u);loadAll();}} onOpenPV={()=>{setTechInt(selInt);setSelInt(null);}} onShowSuggestions={()=>setSuggGeo(selInt)} onExportPDF={()=>setExportPDF(selInt)} onFicheConsigne={()=>{setShowFicheConsigne(selInt);}} onChecklist={()=>setShowChecklist(selInt)} onNonConf={()=>setShowNonConf(selInt)} onZonesCameras={()=>setShowZonesCameras(selInt)}/>}
      {showChecklist && <ChecklistIntervention T={T} intervention={showChecklist} onClose={()=>setShowChecklist(null)} onCompleted={loadAll}/>}
      {showNonConf && <NonConformites T={T} intervention={showNonConf} onClose={()=>setShowNonConf(null)}/>}
      {showSearch && <GlobalSearch T={T} onClose={()=>setShowSearch(false)}
        vehicules={vehiculesGlobal} clients={clients} interventions={interventions} tickets={tickets}
        onNavigate={(v,item)=>{
          if(v==="action"){
            if(item==="action:new-ticket")  { setView("sav");           setShowSAV(true); }
            if(item==="action:new-int")     { setView("interventions");  setShowInt(true); }
            if(item==="action:new-task")    { setView("taches"); }
            if(item==="action:new-client")  { setView("clients"); setShowClient(true); }
          } else {
            setView(v);
            if(item && v==="interventions") setSelInt(item);
            if(item && v==="vehicules")     setPendingVehiculeId(item.id);
          }
        }}/> }
      {showNotif && <div style={{position:"fixed",inset:0,zIndex:400}} onClick={()=>setShowNotif(false)}/>}
      {showInvitation && <ModalInvitation T={T} onClose={()=>setShowInvitation(false)} onDone={loadAll}/>}
      {showFicheConsigne && <FicheConsigne T={T} intervention={showFicheConsigne} onClose={()=>setShowFicheConsigne(null)}/>}
      {showZonesCameras && <ZonesCameras T={T} intervention={showZonesCameras} onClose={()=>setShowZonesCameras(null)}/>}
      {showProfile && <UserProfile T={T} user={user} onClose={()=>setShowProfile(false)} onSaved={(u)=>{
        setUser(u); localStorage.setItem("nexo_user",JSON.stringify(u));
        const thId = u.theme_id || localStorage.getItem("nexo_theme_id") || "gsgroup";
        setThemeId(thId); localStorage.setItem("nexo_theme_id", thId);
      }}/>}
      {exportPDF && <ExportPDF T={T} intervention={exportPDF} onClose={()=>setExportPDF(null)}/>}
      {suggGeo && <SuggestionsGeo T={T} intervention={suggGeo} onSelectInt={i=>setSelInt(i)} onClose={()=>setSuggGeo(null)}/>}
      {techInt && <InterventionTechnicien T={T} intervention={techInt} currentUser={user} onClose={()=>setTechInt(null)} onFinished={()=>{setTechInt(null);loadAll();}}/> }
      <EscaladeNotif T={T} notifs={escaladeNotifs} onDismiss={dismissEscalade}/>
      <AiChatPanel T={T} user={user}/>
    </>
  );
}
